import { useEffect, useMemo, useState } from "react";
import { useHotelContext } from "@/contexts/HotelContext";
import { useDateFormat } from "@/lib/dateFormat";

/** Hotel-timezone business date as YYYY-MM-DD (browser date when the hotel has no timezone). */
export function hotelToday(timeZone) {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timeZone || undefined, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  } catch {
    return new Date().toLocaleDateString("en-CA");
  }
}

export const CHECKIN_GATE_CODE = "CHECKIN_NOT_YET_AVAILABLE";

/**
 * ARRIVAL-GATE · Check-in is only allowed on/after the confirmed arrival date (hotel-local).
 * `today` re-evaluates every 30s so disabled buttons flip on at midnight without a reload.
 */
export function useCheckinGate() {
  const hc = useHotelContext();
  const tz = hc?.ctx?.timezone;
  const { formatDate } = useDateFormat();
  const [today, setToday] = useState(() => hotelToday(tz));
  useEffect(() => {
    setToday(hotelToday(tz));
    const id = setInterval(() => setToday((t) => { const n = hotelToday(tz); return n === t ? t : n; }), 30000);
    return () => clearInterval(id);
  }, [tz]);
  return useMemo(() => {
    const day = (arrival) => (arrival ? String(arrival).slice(0, 10) : null);
    const isBlocked = (arrival) => !!day(arrival) && day(arrival) > today;
    const reason = (arrival) => `Check-in opens at 12:00 AM on ${formatDate(day(arrival))}`;
    return { today, isBlocked, reason, forArrival: (arrival) => ({ blocked: isBlocked(arrival), reason: isBlocked(arrival) ? reason(arrival) : null }) };
  }, [today, formatDate]);
}

/** Wraps a (possibly disabled) button so the block reason shows as a native tooltip. */
export function GateWrap({ blocked, reason, className = "inline-flex", testid, children }) {
  return <span className={className} title={blocked ? reason : undefined} data-testid={testid} data-gated={blocked || undefined}>{children}</span>;
}
